/**
 * AppICEMAP — Permission helpers
 *
 * Client-side checks decide what to SHOW. They are not security: every
 * destructive call is enforced by Directus permissions on the server, so a
 * crafted request from a non-admin still fails there.
 */

/**
 * Is this user a Directus administrator?
 *
 * The flag moved between major versions — Directus 10 keeps `admin_access` on
 * the role, Directus 11 moved it to the policies attached to the user/role — so
 * every known shape is checked instead of betting on one.
 *
 * @param {object|null} user - User record from getMe()
 * @returns {boolean}
 */
export function isAdmin(user) {
	if (!user) return false;

	// Directus 11: admin_access surfaced directly on the user
	if (user.admin_access === true) return true;

	// Directus 10: on the role
	if (user.role?.admin_access === true) return true;

	// Directus 11: on any policy attached to the user or their role
	const policies = [
		...(Array.isArray(user.policies) ? user.policies : []),
		...(Array.isArray(user.role?.policies) ? user.role.policies : []),
	];
	if (policies.some((p) => p?.policy?.admin_access === true || p?.admin_access === true)) {
		return true;
	}

	// Last resort: the role name, for instances that renamed nothing
	const roleName = (user.role?.name || "").toLowerCase();
	return roleName === "administrator" || roleName === "admin";
}

/**
 * May this user delete the given report?
 * Admins only — authors get no delete of their own reports for now, since the
 * confirmation counts of other users hang off them.
 *
 * @param {object|null} user
 * @param {object} _report
 * @returns {boolean}
 */
export function canDeleteReport(user, _report) {
	return isAdmin(user);
}
